import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import type {
  FeedbackItem,
  FeedbackFormData,
  FeedbackSection,
  FeedbackContextValue,
  FeedbackState,
} from '../types';
import { submitFeedback, fetchFeedback, trackEvent } from '../utils';
import { useProposal } from './ProposalContext';

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

interface FeedbackProviderProps {
  children: ReactNode;
}

/**
 * Get access token from URL query params or proposal content
 */
function getAccessToken(proposalAccessToken?: string): string | undefined {
  // First check URL params (e.g., ?token=abc123)
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token');
  if (urlToken) {
    return urlToken;
  }

  // Fall back to proposal content token
  return proposalAccessToken;
}

export function FeedbackProvider({ children }: FeedbackProviderProps) {
  const { proposalId, state: proposalState } = useProposal();

  const [state, setState] = useState<FeedbackState>({
    items: [],
    isSubmitting: false,
    isRefreshing: false,
    isSidebarOpen: false,
    activeSection: null,
    activeLine: null,
  });

  // Get version from loaded proposal
  const proposalVersion = proposalState.status === 'loaded'
    ? proposalState.data.proposal.version
    : 1;

  // Load existing feedback when proposal is loaded
  useEffect(() => {
    if (proposalState.status === 'loaded' && proposalId) {
      // Use internalId for all API calls, not the URL identifier
      const internalId = proposalState.data.proposal.internalId;
      if (!internalId) {
        console.warn('No internal ID available for API calls');
        return;
      }

      // Track first view event
      trackEvent(internalId, 'first_view');

      // Fetch existing feedback (including salesperson responses)
      const token = getAccessToken(proposalState.data.proposal.accessToken);
      fetchFeedback(internalId, token).then(result => {
        if (result.success && result.items) {
          setState(prev => ({ ...prev, items: result.items! }));
        }
      });
    }
  }, [proposalState.status, proposalId]);

  const handleSubmitFeedback = useCallback(async (data: FeedbackFormData) => {
    if (!proposalId || proposalState.status !== 'loaded') return;

    // Use internalId for API calls, not the URL identifier
    const internalId = proposalState.data.proposal.internalId;
    if (!internalId) {
      throw new Error('No internal ID available for feedback submission');
    }

    setState(prev => ({ ...prev, isSubmitting: true }));

    try {
      const result = await submitFeedback(internalId, proposalVersion, data);

      if (result.success) {
        // Add to local state
        const newItem: FeedbackItem = {
          id: result.feedbackId!,
          proposalId,
          section: data.section,
          lineNumber: data.lineNumber,
          comment: data.comment,
          sentiment: data.sentiment,
          createdAt: new Date().toISOString(),
          isRead: false,
        };

        setState(prev => ({
          ...prev,
          items: [...prev.items, newItem],
          isSubmitting: false,
          activeSection: null,
          activeLine: null,
        }));
      } else {
        setState(prev => ({ ...prev, isSubmitting: false }));
        throw new Error(result.error);
      }
    } catch {
      setState(prev => ({ ...prev, isSubmitting: false }));
      throw new Error('Failed to submit feedback');
    }
  }, [proposalId, proposalState, proposalVersion]);

  const openFeedbackModal = useCallback((section: FeedbackSection, lineNumber?: number) => {
    setState(prev => ({
      ...prev,
      activeSection: section,
      activeLine: lineNumber ?? null,
    }));
  }, []);

  const closeFeedbackModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      activeSection: null,
      activeLine: null,
    }));
  }, []);

  const toggleSidebar = useCallback(() => {
    setState(prev => ({
      ...prev,
      isSidebarOpen: !prev.isSidebarOpen,
    }));
  }, []);

  const getFeedbackCount = useCallback((section?: FeedbackSection, lineNumber?: number): number => {
    if (!section) {
      return state.items.length;
    }

    return state.items.filter(item => {
      if (item.section !== section) return false;
      if (lineNumber !== undefined && item.lineNumber !== lineNumber) return false;
      return true;
    }).length;
  }, [state.items]);

  const refreshFeedback = useCallback(async () => {
    if (!proposalId || proposalState.status !== 'loaded') return;

    // Use internalId for API calls, not the URL identifier
    const internalId = proposalState.data.proposal.internalId;
    if (!internalId) {
      console.warn('No internal ID available for feedback refresh');
      return;
    }

    setState(prev => ({ ...prev, isRefreshing: true }));

    try {
      const token = getAccessToken(proposalState.data.proposal.accessToken);
      const result = await fetchFeedback(internalId, token);

      if (result.success && result.items) {
        setState(prev => ({
          ...prev,
          items: result.items!,
          isRefreshing: false,
        }));
      } else {
        setState(prev => ({ ...prev, isRefreshing: false }));
      }
    } catch {
      setState(prev => ({ ...prev, isRefreshing: false }));
    }
  }, [proposalId, proposalState]);

  return (
    <FeedbackContext.Provider
      value={{
        ...state,
        submitFeedback: handleSubmitFeedback,
        openFeedbackModal,
        closeFeedbackModal,
        toggleSidebar,
        getFeedbackCount,
        refreshFeedback,
      }}
    >
      {children}
    </FeedbackContext.Provider>
  );
}

export function useFeedback(): FeedbackContextValue {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
}
